import React, { useEffect, useState } from 'react';

export type NotificationType = 'quest' | 'levelup' | 'warning' | 'achievement' | 'item' | 'success';

interface SystemNotificationProps {
  message: string;
  type: NotificationType;
  onClose: () => void;
  duration?: number;
}

const SystemNotification: React.FC<SystemNotificationProps> = ({ 
  message, 
  type, 
  onClose, 
  duration = 5000 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Slide in
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto close after duration
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'levelup':
        return {
          border: 'border-yellow-500',
          glow: 'shadow-[0_0_30px_rgba(255,215,0,0.6)]',
          bg: 'bg-gradient-to-r from-yellow-900/90 to-orange-900/90',
          iconPath: '/icons/icon-levelup.png',
          text: 'text-yellow-300'
        };
      case 'warning':
        return {
          border: 'border-red-500',
          glow: 'shadow-[0_0_30px_rgba(239,68,68,0.6)]',
          bg: 'bg-gradient-to-r from-red-900/90 to-rose-900/90',
          iconPath: '/icons/icon-notification.png',
          text: 'text-red-300'
        };
      case 'achievement':
        return {
          border: 'border-purple-500',
          glow: 'shadow-[0_0_30px_rgba(168,85,247,0.6)]',
          bg: 'bg-gradient-to-r from-purple-900/90 to-violet-900/90',
          iconPath: '/icons/icon-premium.png',
          text: 'text-purple-300'
        };
      case 'item':
        return {
          border: 'border-cyan-500',
          glow: 'shadow-[0_0_30px_rgba(6,182,212,0.6)]',
          bg: 'bg-gradient-to-r from-cyan-900/90 to-blue-900/90',
          iconPath: '/icons/icon-notification.png',
          text: 'text-cyan-300'
        };
      case 'success':
        return {
          border: 'border-green-500',
          glow: 'shadow-[0_0_30px_rgba(34,197,94,0.6)]',
          bg: 'bg-gradient-to-r from-green-900/90 to-emerald-900/90',
          icon: '✓',
          text: 'text-green-300'
        };
      default: // quest
        return {
          border: 'border-system-blue',
          glow: 'shadow-[0_0_30px_rgba(0,168,255,0.6)]',
          bg: 'bg-gradient-to-r from-blue-900/90 to-indigo-900/90',
          iconPath: '/icons/icon-quest-main.png',
          text: 'text-blue-300'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      className={`
        fixed top-2 right-2 sm:top-4 sm:right-4 z-[9999] max-w-xs sm:max-w-md
        transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-[120%] opacity-0'}
      `}
      style={{ zIndex: 9999 }}
    >
      <div
        className={`
          relative ${styles.bg} ${styles.border} border-2 rounded-lg
          ${styles.glow}
          backdrop-blur-md p-2 sm:p-4
          animate-pulse-glow
        `}
      >
        {/* Hexagonal pattern overlay - Hidden on mobile */}
        <div 
          className="hidden sm:block absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px'
          }}
        />

        {/* Close button - Smaller on mobile */}
        <button
          onClick={handleClose}
          className="absolute top-1 right-1 sm:top-2 sm:right-2 text-gray-400 hover:text-white transition-colors text-lg sm:text-xl"
        >
          ✕
        </button>

        {/* Content - Compact on mobile */}
        <div className="flex items-start gap-1.5 sm:gap-3 relative z-10">
          <div className="flex items-center justify-center animate-bounce flex-shrink-0">
            {styles.iconPath ? (
              <img 
                src={styles.iconPath} 
                alt="Notification"
                className="w-5 h-5 sm:w-8 sm:h-8 object-contain"
                style={{ 
                  filter: `drop-shadow(0 0 8px ${styles.glow.match(/rgba\([^)]+\)/)?.[0] || 'rgba(59, 130, 246, 0.8)'})`
                }}
              />
            ) : (
              <span className="text-lg sm:text-3xl">{styles.icon}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {/* System Notification label - Hidden on mobile */}
            <div className="hidden sm:block text-xs font-orbitron uppercase tracking-wider text-gray-400 mb-1">
              ［ System Notification ］
            </div>
            <div className={`font-rajdhani font-semibold text-xs sm:text-base ${styles.text} leading-tight sm:leading-normal`}>
              {message}
            </div>
          </div>
        </div>

        {/* Animated border glow */}
        <div className="absolute inset-0 rounded-lg opacity-50 animate-pulse pointer-events-none">
          <div className={`absolute inset-0 ${styles.border} border-2 rounded-lg blur-sm`} />
        </div>
      </div>
    </div>
  );
};

// Notification Manager Component
interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

export const SystemNotificationManager: React.FC<{ 
  notifications: Notification[];
  onRemove: (id: string) => void;
}> = ({ notifications, onRemove }) => {
  return (
    <div className="fixed top-0 right-0 z-[9999] pointer-events-none" style={{ zIndex: 9999 }}>
      <div className="flex flex-col gap-1.5 sm:gap-3 p-2 sm:p-4 pointer-events-auto">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            style={{
              marginTop: index > 0 ? '8px' : '0',
            }}
          >
            <SystemNotification
              message={notification.message}
              type={notification.type}
              onClose={() => onRemove(notification.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemNotification;




